import { Component, type ErrorInfo, type ReactNode } from "react";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
  resetKey: number;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    error: null,
    resetKey: 0,
  };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Admin UI render failure", error, info.componentStack);
  }

  private retry = () => {
    this.setState((current) => ({
      error: null,
      resetKey: current.resetKey + 1,
    }));
  };

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
              <i className="ri-error-warning-line" />
              Admin UI failed to load
            </div>
            <p className="mt-2 text-sm text-slate-600">
              The page could not be rendered. Retry once, or reload the console if
              the app was updated while this tab was open.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={this.retry}
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={this.reload}
                className="rounded border border-transparent bg-coral-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-coral-700"
              >
                Reload console
              </button>
            </div>
          </div>
        </div>
      );
    }

    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}
